/**
 * Attachment Context Tracker
 * Prevents token cost loophole by tracking follow-up questions about attachments
 */

export interface AttachmentContext {
    sessionId: string;
    lastAttachmentMessageId: string;
    attachmentCount: number;
    followUpCount: number;
    timestamp: number;
}

const CONTEXT_TIMEOUT = 10 * 60 * 1000; // 10 minutes

/**
 * Track when a message with attachments is sent
 */
export function trackAttachmentMessage(
    sessionId: string,
    messageId: string,
    attachmentCount: number
): void {
    const context: AttachmentContext = {
        sessionId,
        lastAttachmentMessageId: messageId,
        attachmentCount,
        followUpCount: 0,
        timestamp: Date.now()
    };
    
    localStorage.setItem(`attachment_context_${sessionId}`, JSON.stringify(context));
}

/**
 * Check if there's an active attachment context and increment follow-up count
 */
export function checkAndIncrementAttachmentContext(sessionId: string): {
    hasContext: boolean;
    followUpCount: number;
} {
    try {
        const stored = localStorage.getItem(`attachment_context_${sessionId}`);
        if (!stored) {
            return { hasContext: false, followUpCount: 0 };
        }
        
        const context: AttachmentContext = JSON.parse(stored);
        
        // Check if context has expired
        if (Date.now() - context.timestamp > CONTEXT_TIMEOUT) {
            localStorage.removeItem(`attachment_context_${sessionId}`);
            return { hasContext: false, followUpCount: 0 };
        }
        
        // Increment follow-up count
        context.followUpCount += 1;
        context.timestamp = Date.now(); // Refresh timestamp
        
        localStorage.setItem(`attachment_context_${sessionId}`, JSON.stringify(context));
        
        return {
            hasContext: true,
            followUpCount: context.followUpCount
        };
    } catch (error) {
        console.error('Failed to check attachment context:', error);
        return { hasContext: false, followUpCount: 0 };
    }
}

/**
 * Clear attachment context (e.g., when user sends new attachments)
 */
export function clearAttachmentContext(sessionId: string): void {
    localStorage.removeItem(`attachment_context_${sessionId}`);
}

/**
 * Get current attachment context without modifying it
 */
export function getAttachmentContext(sessionId: string): AttachmentContext | null {
    try {
        const stored = localStorage.getItem(`attachment_context_${sessionId}`);
        if (!stored) return null;
        
        const context: AttachmentContext = JSON.parse(stored);
        
        // Check if context has expired
        if (Date.now() - context.timestamp > CONTEXT_TIMEOUT) {
            localStorage.removeItem(`attachment_context_${sessionId}`);
            return null;
        }
        
        return context;
    } catch (error) {
        console.error('Failed to get attachment context:', error);
        return null;
    }
}
